import { sum, multiply, divide, subtract } from "./core.js"

/**
 * A basic arithmetic operation applied to every element of a sub-array.
 *
 * `rootFieldsToOperate` names fields that live on the document rather than on
 * the element — the exchange rate is the document's, not the VAT group's — and
 * they join the element's own fields as operands.
 *
 * Both of those are read alongside the element rather than written into it or
 * into the config. `operation` is the object literal from `logicData.js`, a
 * module-level constant shared by every document this process handles, so
 * pushing the root fields into `fieldsToOperate` grew that array by one entry
 * per element for the life of the process: ["vatTaxAmount"] became
 * ["vatTaxAmount","exchangeRate","exchangeRate"] after a single two-group
 * invoice. It stayed correct only because `divide` reads the first two entries
 * and ignores the rest (36.03 §7 B2).
 */
const subBasic = (data, operation) => {
    const { fieldsToOperate, targetField, basicOp, subEntity, rootFieldsToOperate } = operation

    if (!data || !data[subEntity] || !Array.isArray(data[subEntity])) {
        return data
    }

    let runOp

    switch (basicOp) {
        case 'sum':
            runOp = sum
            break;
        case 'multiply':
            runOp = multiply
            break;
        case 'divide':
            runOp = divide
            break;
        case 'subtract':
            runOp = subtract
            break;
        default:
            runOp = sum
            break;
    }

    // Built once per call, from the config rather than into it.
    const fields = rootFieldsToOperate
        ? [...fieldsToOperate, ...rootFieldsToOperate]
        : fieldsToOperate

    const newSubArray = data[subEntity].map(elem => {
        // The operand is a copy: the root values are needed to compute with, not
        // to keep. Writing them onto the element left `exchangeRate` sitting on
        // every VAT group in the form store — stripped by AJV before it could
        // reach the database, and never a VAT group's business either way.
        const operand = rootFieldsToOperate
            ? { ...elem, ...Object.fromEntries(rootFieldsToOperate.map(field => [field, data[field]])) }
            : elem

        let result = runOp(operand, fields)
        if (isNaN(result)) result = 0

        return {
            ...elem,
            [targetField]: result
        }
    })

    return {
        ...data,
        [subEntity]: newSubArray
    }
}

export default subBasic
